import React, { useRef, useEffect, useCallback } from 'react';
import FormCard from './FormCard';

interface AISuggestOffFormTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showAIButton?: boolean;
  minHeight?: string;
  textareaRef?: (el: HTMLTextAreaElement | null) => void;
  onTextareaChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onAIConsult?: () => void;
  helperText?: string | React.ReactNode;
  highlightPlaceholders?: boolean; // {役職名を入力}を赤色でハイライト
  readOnly?: boolean;
}

const AISuggestOffFormTextarea: React.FC<AISuggestOffFormTextareaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  showAIButton = false,
  minHeight = '150px',
  textareaRef,
  onTextareaChange,
  onAIConsult,
  helperText,
  highlightPlaceholders = false,
  readOnly = false,
}) => {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  // 自動リサイズ関数
  const adjustHeight = useCallback(() => {
    const textarea = internalRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  // 値が変更されたときに高さを調整
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('[AISuggestOffFormTextarea] handleChange called:', { value: e.target.value.substring(0, 50) });
    onChange(e.target.value);
    adjustHeight();
    if (onTextareaChange) {
      onTextareaChange(e);
    }
  };

  return (
    <FormCard>
      {/* Form Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-3">
        {/* Heading */}
        <h3
          className="text-lg font-bold"
          style={{
            fontFamily: 'Noto Sans JP',
            color: '#0D357F',
          }}
        >
          {label}
        </h3>

        {/* AI Button */}
        {showAIButton && (
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{
              background: '#0D357F',
              opacity: onAIConsult ? 1 : 0.6,
              cursor: onAIConsult ? 'pointer' : 'default',
              fontFamily: 'Noto Sans JP',
            }}
            onClick={onAIConsult}
            disabled={!onAIConsult}
          >
            AI相談
          </button>
        )}
      </div>

      {/* Helper Text */}
      {helperText && (
        <div className="mb-2 text-sm text-gray-600">
          {helperText}
        </div>
      )}

      {/* Textarea Field */}
      <div className="relative">
        {highlightPlaceholders && (
          <div
            className="absolute inset-0 px-3 py-3 pointer-events-none whitespace-pre-wrap break-words"
            style={{
              fontFamily: 'Noto Sans JP',
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              minHeight: minHeight,
              backgroundColor: '#FFFFFF',
            }}
          >
            {value.split(/(\{[^}]+\}|｛[^｝]+｝)/g).map((part, index) => (
              /^(\{[^}]+\}|｛[^｝]+｝)$/.test(part) ? (
                <span key={index} style={{ color: '#DC2626' }}>{part}</span>
              ) : (
                <span key={index} style={{ color: '#000000' }}>{part}</span>
              )
            ))}
          </div>
        )}
        <textarea
          ref={(el) => {
            internalRef.current = el;
            if (textareaRef) {
              textareaRef(el);
            }
          }}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`w-full px-3 py-3 border border-gray-300 rounded-lg text-sm ${highlightPlaceholders ? 'text-transparent relative z-10' : 'text-black'} ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
          style={{
            fontFamily: 'Noto Sans JP',
            resize: 'none',
            minHeight: minHeight,
            overflow: 'hidden',
            caretColor: highlightPlaceholders ? '#000000' : undefined,
            backgroundColor: highlightPlaceholders ? 'transparent' : (readOnly ? '#F9FAFB' : undefined),
          }}
        />
      </div>
    </FormCard>
  );
};

export default AISuggestOffFormTextarea;
